import React, { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import { apiErrorMessage, validateImageFile } from "Helpers/ContentValidation";

import { Grid, TextField } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

/**
 * Image field for content that is stored as JSON rather than as its own record
 * (page sections and their rows).
 *
 * A brand logo or product image rides along with the record it belongs to, so
 * it uses a plain file input. A section image cannot: the section only holds a
 * path. This component uploads the file first (POST /media/upload) and reports
 * the returned path back through `onChange`.
 *
 * The path stays editable by hand so existing website assets ("/images/...")
 * can be referenced without re-uploading them.
 */
export default class ImagePickerField extends Component {
  state = {
    uploading: false,
    error: "",
  };

  handleFile = async (event) => {
    const file = event.target.files?.[0];
    // Clear the input so picking the same file twice still fires a change.
    event.target.value = "";
    if (!file) return;

    const fileError = validateImageFile(file);
    if (fileError) {
      this.setState({ error: fileError });
      this.props.onError(fileError);
      return;
    }

    if (init() !== "success") {
      const message = "Your session has expired — please sign in again";
      this.setState({ error: message });
      this.props.onError(message);
      return;
    }

    this.setState({ uploading: true, error: "" });

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.post(
        `${Constants.postUrls.uploadMedia}?folder=${this.props.folder}`,
        formData
      );

      const path = response?.data?.data?.path;
      if (!path) throw new Error("Upload did not return an image path");

      this.setState({ uploading: false });
      this.props.onChange(path);
    } catch (error) {
      const message = apiErrorMessage(error, "Image upload failed");
      this.setState({ uploading: false, error: message });
      this.props.onError(message);
    }
  };

  render() {
    const { label, value, onChange, helperText } = this.props;
    const { uploading, error } = this.state;

    // Uploaded paths need the API host in front; website asset paths and full
    // URLs are shown as they are.
    const previewSrc = value?.startsWith("/uploads/")
      ? Constants.imageDomain + value
      : value;

    return (
      <MDBox>
        <MDTypography variant="button" fontWeight="medium" display="block">
          {label}
        </MDTypography>

        {helperText ? (
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            {helperText}
          </MDTypography>
        ) : null}

        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              label="Image path"
              value={value}
              placeholder="/uploads/pages/example.png"
              onChange={(event) => onChange(event.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={5}>
            <MDBox display="flex" gap={1} alignItems="center">
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Upload"}
                <input type="file" accept="image/*" hidden onChange={this.handleFile} />
              </MDButton>

              {value ? (
                <MDButton
                  variant="text"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => onChange("")}
                >
                  Clear
                </MDButton>
              ) : null}
            </MDBox>
          </Grid>
        </Grid>

        {error ? (
          <MDTypography variant="caption" color="error" display="block" mt={1}>
            {error}
          </MDTypography>
        ) : null}

        {previewSrc ? (
          <MDBox mt={1}>
            <img
              src={previewSrc}
              alt={label}
              style={{
                height: 90,
                maxWidth: "100%",
                objectFit: "contain",
                borderRadius: 6,
                border: "1px solid #e0e0e0",
                background: "#fafafa",
              }}
            />
          </MDBox>
        ) : null}
      </MDBox>
    );
  }
}

ImagePickerField.defaultProps = {
  folder: "pages",
  helperText: "",
  onError: () => {},
};

ImagePickerField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  // Upload target folder — the API only accepts brands, brand_products, pages.
  folder: PropTypes.oneOf(["brands", "brand_products", "pages"]),
  helperText: PropTypes.string,
  onError: PropTypes.func,
};
